import { SHOP_RATINGS_API_URL, fetchRatingsSummary, fetchReviewList } from 'webwinkelkeur-api';

const MIN_RATING = 5;
const MAX_REVIEWS = 4;

class ReviewQuotes extends HTMLElement {
  // Shared counter so content ids stay unique across multiple sections on a page
  static quoteId = 0;

  async connectedCallback() {
    this.list = this.querySelector('[data-review-quotes-list]');
    this.template = this.querySelector('template[data-review-quotes-item]');

    if (!this.list || !this.template) return;

    try {
      const [reviews, summary] = await Promise.all([this.fetchReviews(), fetchRatingsSummary()]);
      if (!reviews.length) return;

      this.renderReviews(reviews);
      this.replaceTotals(summary);
      this.hidden = false;
      this.updateSwiper();
    }
    catch (error) {
      // Leave the section hidden when the API is unavailable
      console.warn('Review quotes could not be loaded:', error);
    }
  }

  // Always shop-wide reviews: product pages already show their own product reviews
  async fetchReviews() {
    return fetchReviewList(SHOP_RATINGS_API_URL, {
      listKey: 'ratings',
      keep: entry => this.shopReview(entry),
      max: MAX_REVIEWS,
    });
  }

  // Shop ratings can have an empty comment (rating only)
  shopReview(rating) {
    if (rating.rating < MIN_RATING || !rating.comment) return null;

    return { name: rating.name ?? '',
      rating: rating.rating,
      text: rating.comment,
      date: rating.date };
  }

  // Replace the [rating] and [count] placeholders in the section copy: on product
  // pages the product's own totals (from the app widget metafield, falling back
  // to the shop-wide totals when a product has no reviews yet), elsewhere the
  // shop-wide totals. Note the scales differ: product ratings are 0–5, the
  // shop-wide rating is 0–10.
  replaceTotals(summary) {
    const locale = this.dataset.locale;
    const productRating = Number.parseFloat(this.dataset.productRating);
    const productCount = Number.parseInt(this.dataset.productRatingCount, 10);
    const useProduct = !Number.isNaN(productRating) && !Number.isNaN(productCount);

    const ratingValue = useProduct ? productRating : summary.rating_average;
    const countValue = useProduct ? productCount : summary.amount;

    const rating = ratingValue.toLocaleString(locale, { minimumFractionDigits: 1,
      maximumFractionDigits: 1 });
    const count = countValue.toLocaleString(locale);

    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.nodeValue.includes('[rating]') && !node.nodeValue.includes('[count]')) continue;
      node.nodeValue = node.nodeValue.replaceAll('[rating]', rating).replaceAll('[count]', count);
    }
  }

  renderReviews(reviews) {
    for (const review of reviews) {
      const item = this.template.content.cloneNode(true);

      item.querySelector('[data-review-quote-rating]').textContent = `${review.rating}/5`;
      item.querySelector('[data-review-quote-name]').textContent = review.name;
      this.renderDate(item.querySelector('[data-review-quote-date]'), review.date);

      // Wire up the clamp toggle's aria-controls with a unique content id per quote
      const text = item.querySelector('[data-review-quote-text]');
      text.textContent = review.text;
      text.id = `review-quote-text-${ReviewQuotes.quoteId += 1}`;
      item.querySelector('[data-text-clamp-toggle]')?.setAttribute('aria-controls', text.id);

      this.list.append(item);
    }
  }

  // Shown as e.g. "juli 2026" (the API exposes no reviewer details beyond the
  // name), with the full date in the time element's datetime attribute
  renderDate(element, value) {
    const date = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return;

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    element.dateTime = `${date.getFullYear()}-${month}-${day}`;
    element.textContent = new Intl.DateTimeFormat(this.dataset.locale, { month: 'long',
      year: 'numeric' }).format(date);
  }

  // The swiper initialised while the section was empty and hidden; re-measure now
  updateSwiper() {
    const slider = this.querySelector('swiper-slider');
    if (slider?.swiperInstance) slider.updateSwiper();
  }
}

if (!customElements.get('review-quotes')) {
  customElements.define('review-quotes', ReviewQuotes);
}
